<script lang="ts" setup>
import { z } from 'zod'

defineProps({
  size: {
    default: 'md',
    type: String as PropType<UInputSize>,
  },
})

const { createEvent } = useAnalytics()
const { createToast } = useToasts()
const { t } = useI18n()
const guards = useGuards()
const teamsStore = useTeamsStore()

const creating = ref(false)
const form = reactive({ name: '' })

const validation = z.object({
  name: z.string()
    .min(1, { message: t('validation.required') })
    .max(255, { message: t('validation.max', { length: 255 }) }),
})

async function onCreateApiToken() {
  if (guards.userOnly() && !creating.value) {
    creating.value = true

    try {
      await teamsStore.createTeamApiToken({ name: form.name })

      createToast({ message: 'teamApiTokenCreated' })
      createEvent({ name: '[Settings] Create Team API Token' })

      form.name = ''
    }
    catch {}

    creating.value = false
  }
}
</script>

<template>
  <UForm
    class="flex items-start gap-2"
    :schema="validation"
    :state="form"
    :validate-on="['submit']"
    @submit="onCreateApiToken"
  >
    <UFormGroup
      class="w-full"
      name="name"
    >
      <UInput
        v-model="form.name"
        autocomplete="off"
        :icon="getIcon('apiToken')"
        :placeholder="t('enterName')"
        :size="size"
      />
    </UFormGroup>

    <UButton
      :icon="getIcon('check')"
      :label="t('createToken')"
      :loading="creating"
      :size="size"
      type="submit"
      :ui="{ icon: { base: 'text-green-600 dark:text-green-400' } }"
    />
  </UForm>
</template>

<i18n lang="json">
{
  "en": {
    "createToken": "Create token",
    "enterName": "Enter a name"
  },
  "es": {
    "createToken": "Crear token",
    "enterName": "Introduce un nombre"
  },
  "fr": {
    "createToken": "Créer un jeton",
    "enterName": "Entrez un nom"
  }
}
</i18n>
